import { Stack } from '@mui/material';

import type { RankedCandidate } from '@/api/contact-import/types';
import BriefcaseIcon from '@/assets/svg/ui-elements/briefcase.svg';
import BuildingIcon from '@/assets/svg/ui-elements/building.svg';
import GlobeIcon from '@/assets/svg/ui-elements/globe.svg';
import LocationIcon from '@/assets/svg/ui-elements/location.svg';
import MailIcon from '@/assets/svg/ui-elements/mail.svg';
import PhoneIcon from '@/assets/svg/ui-elements/phone.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import { Badge, Drawer, RadioGroup, theme, Typography } from '@/shared/design-system';

import { CandidateCard } from '../../../match-check/candidate-card';
import { FieldRow } from '../../../match-check/field-row';
import { NewContactCard } from '../../../match-check/new-contact-card';
import { VerdictLegend } from '../../../match-check/verdict-legend';
import type { ContactData } from '../../types';
import { CandidatesPane, ImportedNameRow, ImportedPane, MatchPanes } from './styled';

export const NEW_CONTACT_VALUE = 'new';

interface MatchCheckDrawerProps {
  open: boolean;
  data: Partial<ContactData>;
  candidates: RankedCandidate[];
  sourceLabel: string;
  isSubmitting?: boolean;
  selection: string;
  onSelectionChange: (_selection: string) => void;
  onContinue: (_contactId: string | null) => void;
  onBack: () => void;
  onClose: () => void;
}

export const MatchCheckDrawer = ({
  open,
  data,
  candidates,
  sourceLabel,
  isSubmitting,
  selection,
  onSelectionChange,
  onContinue,
  onBack,
  onClose,
}: MatchCheckDrawerProps) => {
  const isCreatingNew = selection === NEW_CONTACT_VALUE;
  const hasCandidates = candidates.length > 0;
  const isMatching = hasCandidates && !isCreatingNew;

  const emails = (data.emails ?? []).filter(Boolean).map((value) => ({ value }));
  const phones = (data.phones ?? []).filter(Boolean).map((value) => ({ value }));
  const websites = (data.websites ?? []).filter(Boolean).map((value) => ({ value }));
  const addresses = (data.addresses ?? [])
    .map((address) =>
      [
        address.street,
        address.street2,
        address.city,
        address.state,
        address.postalCode,
        address.country,
      ]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(', '),
    )
    .filter(Boolean)
    .map((value) => ({ value }));

  return (
    <Drawer
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={
        isMatching ? 'We found some contacts that might match' : 'You are creating a new contact'
      }
      description={
        isMatching
          ? 'Select the right one, or confirm this is a new contact.'
          : hasCandidates
            ? 'This will be added as a new person. Pick a candidate above to merge instead.'
            : 'Nothing in your address book matches these details. Review them and confirm.'
      }
      maxWidth={864}
      secondaryAction={{ children: 'Back', color: 'tertiary', onClick: onBack }}
      primaryAction={{
        children: isCreatingNew ? 'Add Contact' : 'Continue',
        disabled: isSubmitting,
        onClick: () => onContinue(isCreatingNew ? null : selection),
      }}
    >
      <MatchPanes>
        <ImportedPane>
          <ImportedNameRow>
            <Typography variant="body1" fontWeight={600}>
              {data.name?.trim() || 'Unnamed contact'}
            </Typography>
            <Badge label={`from ${sourceLabel}`} size="sm" />
          </ImportedNameRow>

          <FieldRow icon={<MailIcon width={16} height={16} />} label="Email" values={emails} />
          <FieldRow icon={<PhoneIcon width={16} height={16} />} label="Phone" values={phones} />
          <FieldRow
            icon={<BuildingIcon width={16} height={16} />}
            label="Company"
            values={data.organizationName?.trim() ? [{ value: data.organizationName.trim() }] : []}
          />
          <FieldRow
            icon={<BriefcaseIcon width={16} height={16} />}
            label="Title"
            values={data.jobTitle?.trim() ? [{ value: data.jobTitle.trim() }] : []}
          />
          <FieldRow icon={<GlobeIcon width={16} height={16} />} label="Website" values={websites} />
          <FieldRow
            icon={<LocationIcon width={16} height={16} />}
            label="Address"
            values={addresses}
          />
        </ImportedPane>

        <CandidatesPane>
          <Typography variant="body3" color={theme.colors.text.secondary}>
            {hasCandidates
              ? 'Which existing contact does this match? Pick one, or choose to add as new.'
              : 'No existing contact matched, so this will be added as a new person.'}
          </Typography>

          {hasCandidates && <VerdictLegend />}

          <RadioGroup value={selection} onValueChange={onSelectionChange}>
            <Stack flexDirection="column" gap="12px">
              {candidates.map((candidate) => (
                <CandidateCard
                  key={candidate.contactId}
                  candidate={candidate}
                  radioValue={candidate.contactId}
                />
              ))}

              <NewContactCard
                radioValue={NEW_CONTACT_VALUE}
                icon={<PlusIcon width={16} height={16} />}
              />
            </Stack>
          </RadioGroup>
        </CandidatesPane>
      </MatchPanes>
    </Drawer>
  );
};
